import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { NAV_LINKS, EDITORIAL } from "../../data/content";
import { BuyButton } from "./ui";
import { trackCircleCta } from "../../lib/trackCircleCta";

export function Footer() {
  return (
    <footer id="contact" className="relative bg-primary border-t border-[var(--gold-line)] py-16" data-testid="footer" data-section="footer" data-section-label="Footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-2">
            <p className="font-display text-cream text-xl tracking-[0.2em]">
              SELF DOUBLING<span className="text-gold">©</span>
            </p>
            <p className="font-editorial italic text-secondary text-lg mt-3 max-w-sm">
              The Truth That Dwells Within You — a new frontier in psychodrama, trauma and
              embodied memory.
            </p>
            <p className="font-body text-muted text-sm mt-4">By Maria Gioia Mosca · 2011–2026</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <BuyButton location="footer" size="md" label="Buy on Amazon" testId="buy-btn-footer" />
              <Link
                to="/growing-circle"
                data-testid="growing-circle-btn-footer"
                onClick={() => trackCircleCta("footer")}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md font-label text-[0.68rem] tracking-[0.18em] uppercase text-[#f5ecdf] ring-1 ring-inset ring-[rgba(122,30,47,0.85)] shadow-[0_2px_10px_-3px_rgba(122,30,47,0.55)] hover:ring-[rgba(154,42,63,1)] hover:shadow-[0_4px_18px_-4px_rgba(154,42,63,0.75)] transition-all duration-300"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, rgba(154,42,63,0.95) 0%, rgba(90,26,36,0.95) 100%)",
                }}
              >
                <span>Self Doubling Growing Circle</span>
                <ArrowUpRight
                  size={13}
                  aria-hidden
                  strokeWidth={1.75}
                  className="-mr-0.5 opacity-90"
                />
              </Link>
            </div>
            <p className="font-body text-sm text-muted mt-5">
              Contact:{" "}
              <a
                href={`mailto:${EDITORIAL.contact}`}
                className="text-gold-light hover:text-gold transition-colors"
                data-testid="contact-email"
              >
                {EDITORIAL.contact}
              </a>
            </p>
          </div>

          <div>
            <p className="label mb-4">Explore</p>
            <ul className="space-y-2.5">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="font-body text-sm text-secondary hover:text-gold transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="label mb-4">Editorial Data</p>
            <ul className="space-y-2 font-body text-sm text-muted" data-testid="editorial-data">
              <li className="text-secondary italic font-editorial text-base">{EDITORIAL.title}</li>
              <li>Author: {EDITORIAL.author}</li>
              <li>ISBN (paperback): {EDITORIAL.isbnPaperback}</li>
              <li>ISBN (eBook): {EDITORIAL.isbnEbook}</li>
              <li>Deposit: {EDITORIAL.deposit}</li>
            </ul>
            <p className="label mt-6 mb-3">Coming soon</p>
            <p className="font-body text-sm text-muted">Research · Articles · Training · Italiano</p>
          </div>
        </div>

        {/* MGM&AT Brand Communication™ — creative credit row.
            Placed AFTER the three-column footer and BEFORE the horizontal
            divider. Logo on the left (black plate, TM + Brand Communication
            in white, other colours untouched), text credit on the right in
            the footer-label style, two steps larger than the tiny copyright. */}
        <div
          className="mt-14 flex flex-col md:flex-row md:items-center gap-x-8 gap-y-5"
          data-testid="mgmat-credit"
        >
          <img
            src="/mgmat-brand-communication.png?v=9"
            alt="MGM&AT Brand Communication — The Fastest Way to Be You"
            className="h-36 sm:h-32 md:h-36 w-auto max-w-full sm:max-w-none shrink-0 select-none rounded-sm mx-auto md:mx-0"
            draggable="false"
            data-testid="mgmat-logo"
          />
          <div className="flex-1 min-w-0 space-y-2">
            <p className="font-editorial font-bold text-base sm:text-lg text-cream leading-snug">
              Created, designed and developed by MGM&amp;AT Brand Communication
            </p>
            <p className="font-editorial font-bold text-base sm:text-lg text-cream leading-snug">
              Original brand identity and creative concept documented through two timestamped deposits on Patamu.
            </p>
            <p className="font-editorial font-bold text-base sm:text-lg text-cream leading-snug">
              © 2026 MGM&amp;AT Brand Communication. All rights reserved.
            </p>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-[var(--gold-line)]">
          <ul
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-4"
            data-testid="footer-legal-links"
          >
            <li>
              <a
                href="https://www.iubenda.com/privacy-policy/84007906"
                target="_blank"
                rel="noopener noreferrer"
                className="font-label text-[0.65rem] text-muted tracking-[0.14em] hover:text-gold transition-colors"
                data-testid="footer-privacy-policy"
              >
                PRIVACY POLICY
              </a>
            </li>
            <li aria-hidden="true" className="font-label text-[0.65rem] text-[var(--gold-line)] tracking-[0.14em]">·</li>
            <li>
              <a
                href="https://www.iubenda.com/privacy-policy/84007906/cookie-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-label text-[0.65rem] text-muted tracking-[0.14em] hover:text-gold transition-colors"
                data-testid="footer-cookie-policy"
              >
                COOKIE POLICY
              </a>
            </li>
            <li aria-hidden="true" className="font-label text-[0.65rem] text-[var(--gold-line)] tracking-[0.14em]">·</li>
            <li>
              <a
                href="https://www.iubenda.com/terms-and-conditions/84007906"
                target="_blank"
                rel="noopener noreferrer"
                className="font-label text-[0.65rem] text-muted tracking-[0.14em] hover:text-gold transition-colors"
                data-testid="footer-terms-conditions"
              >
                TERMS &amp; CONDITIONS
              </a>
            </li>
          </ul>
          <p className="font-label text-[0.65rem] text-muted tracking-[0.14em] text-center">
            © {new Date().getFullYear()}{" "}
            <span className="whitespace-nowrap">MARIA GIOIA MOSCA</span>{" · "}
            <span className="whitespace-nowrap">SELF DOUBLING©</span>{" · "}
            <span className="whitespace-nowrap">MOSCA METHOD©</span>{" · "}
            <span className="whitespace-nowrap">ALL RIGHTS RESERVED</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
